import { createServerFn } from "@tanstack/react-start";
import { requireSupabaseAuth } from "@/integrations/supabase/auth-middleware";

function makeToken() {
  // Cryptographically secure ~128-bit token (32 hex chars).
  const bytes = new Uint8Array(16);
  crypto.getRandomValues(bytes);
  return Array.from(bytes, (b) => b.toString(16).padStart(2, "0")).join("");
}

export const createWishlistShare = createServerFn({ method: "POST" })
  .middleware([requireSupabaseAuth])
  .handler(async ({ context }) => {
    const { supabase, userId } = context;
    const { data: items, error: itemsErr } = await supabase
      .from("wishlist_items")
      .select("product_id")
      .eq("user_id", userId);
    if (itemsErr) throw new Error(itemsErr.message);
    const productIds = (items ?? []).map((r: any) => r.product_id);

    const token = makeToken();
    const { error } = await supabase
      .from("wishlist_shares")
      .insert({ token, user_id: userId, product_ids: productIds });
    if (error) throw new Error(error.message);
    return { token };
  });

export const getWishlistShare = createServerFn({ method: "GET" })
  .inputValidator((data: { token: string }) => {
    if (!data?.token || typeof data.token !== "string" || data.token.length > 64) {
      throw new Error("Invalid token");
    }
    return { token: data.token };
  })
  .handler(async ({ data }) => {
    const { supabaseAdmin } = await import("@/integrations/supabase/client.server");
    const { data: share, error } = await supabaseAdmin
      .from("wishlist_shares")
      .select("token, product_ids, created_at")
      .eq("token", data.token)
      .maybeSingle();
    if (error) throw new Error(error.message);
    if (!share) return { share: null, products: [] };

    const ids = (share.product_ids ?? []) as string[];
    if (ids.length === 0) return { share, products: [] };

    const { data: rows } = await supabaseAdmin
      .from("products")
      .select("*, creator_products(creators:creator_id(handle, name))")
      .in("id", ids);

    const products = (rows ?? []).map((row: any) => {
      const cp = Array.isArray(row.creator_products) ? row.creator_products[0] : null;
      const c = cp?.creators ?? null;
      const { creator_products, ...rest } = row;
      return { ...rest, creator_handle: c?.handle ?? null, creator_name: c?.name ?? null };
    });
    return { share, products };
  });
